import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { ElMessage } from "element-plus";
import { getAccessToken, useSimpleAuthStore } from "@shared/stores";

export interface AuthRetryConfig extends InternalAxiosRequestConfig {
  _retryAuth?: boolean;
  /**
   * 跳过 401 会话恢复链（面板级自助改密等场景）。
   * 背景：旧密码错误时后端返回 401，若走恢复链会 refresh→重放→再 401→logout 跳登录页，
   * 把「输错密码」误判为「会话过期」。置 true 的请求由调用方自行展示错误文案。
   */
  _skipAuthRecovery?: boolean;
  silent?: boolean;
  _skipGlobalError?: boolean;
}

let redirectScheduled = false;

export function attachAccessToken(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  if (!config.headers) {
    config.headers = {} as any;
  }
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

export function redirectToLogin(message = "登录已过期，请重新登录"): void {
  if (window.location.pathname.includes("/login")) return;
  if (redirectScheduled) return;

  redirectScheduled = true;
  ElMessage.info(message);
  window.setTimeout(() => {
    redirectScheduled = false;
    window.location.href = "/login";
  }, 800);
}

export async function tryRecoverSession(
  instance: AxiosInstance,
  error: AxiosError,
): Promise<unknown | null> {
  const originalConfig = error.config as AuthRetryConfig | undefined;

  if (error.response?.status !== 401 || !originalConfig) {
    return null;
  }

  // 面板级豁免：不进入恢复链，401 交由调用方翻译（如「当前密码不正确」）
  if (originalConfig._skipAuthRecovery) {
    return null;
  }

  if (originalConfig._retryAuth) {
    const authStore = useSimpleAuthStore();
    await authStore.logout({ suppressMessage: true });
    redirectToLogin();
    return null;
  }

  const authStore = useSimpleAuthStore();
  const refreshed = await authStore.refreshSession();
  const refreshedToken = getAccessToken();
  if (!refreshed || !refreshedToken) {
    await authStore.logout({ suppressMessage: true });
    redirectToLogin();
    return null;
  }

  originalConfig._retryAuth = true;
  if (!originalConfig.headers) {
    originalConfig.headers = {} as any;
  }
  originalConfig.headers.Authorization = `Bearer ${refreshedToken}`;
  return instance.request(originalConfig);
}
